// src/pages/ProductForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Card,
  CardMedia,
  FormControlLabel,
  Checkbox,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import { ArrowBack, Save, Delete, AddPhotoAlternate, Language } from '@mui/icons-material';

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Taille Unique'];
const CATEGORY_OPTIONS = [
  { id: 'tshirts', label: { fr: 'T-shirts', en: 'T-shirts', nl: 'T-shirts' } },
  { id: 'sweats', label: { fr: 'Sweats', en: 'Hoodies', nl: 'Truien' } },
  { id: 'accessoires', label: { fr: 'Accessoires', en: 'Accessories', nl: 'Accessoires' } },
  { id: 'goodies', label: { fr: 'Goodies', en: 'Goodies', nl: 'Goodies' } }
];

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' }
];

function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeLang, setActiveLang] = useState('fr');

  const [formData, setFormData] = useState({
    title_i18n: { fr: '', en: '', nl: '' },
    description_i18n: { fr: '', en: '', nl: '' },
    title: '',
    description: '',
    price: 0,
    comparePrice: 0,
    category: '',
    sizes: [],
    colors: [''],
    inventory: 10,
    featured: false,
    status: 'draft',
    images: [],
    slug: ''
  });

  const [newColor, setNewColor] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const productRef = doc(db, 'products', id);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          
          const title_i18n = productData.title_i18n || { fr: productData.title || '', en: '', nl: '' };
          const description_i18n = productData.description_i18n || { fr: productData.description || '', en: '', nl: '' };

          setFormData({
            title_i18n,
            description_i18n,
            title: title_i18n.fr || '',
            description: description_i18n.fr || '',
            price: productData.price || 0,
            comparePrice: productData.comparePrice || 0,
            category: productData.category || '',
            sizes: productData.sizes || [],
            colors: productData.colors || [''],
            inventory: productData.inventory || 10,
            featured: productData.featured || false,
            status: productData.status || 'draft',
            images: productData.images || [],
            slug: productData.slug || ''
          });
        }
      } catch (err) {
        setError('Erreur lors du chargement du produit');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleLangChange = (field, value, lang = activeLang) => {
    setFormData(prev => {
      const next = { ...prev };
      next[`${field}_i18n`] = { ...prev[`${field}_i18n`], [lang]: value };
      
      // Si on modifie le français, mettre à jour les champs par défaut
      if (lang === 'fr' && field === 'title') {
        next.title = value;
      }
      if (lang === 'fr' && field === 'description') {
        next.description = value;
      }
      
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSizeChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, sizes: typeof value === 'string' ? value.split(',') : value }));
  };

  const addColor = () => {
    if (newColor.trim() && !formData.colors.includes(newColor.trim())) {
      setFormData(prev => ({
        ...prev,
        colors: [...prev.colors, newColor.trim()]
      }));
      setNewColor('');
    }
  };

  const removeColor = (colorToRemove) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(color => color !== colorToRemove)
    }));
  };

  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const safeName = file.name
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9._-]/g, '_');

        const storageRef = ref(storage, `products/${Date.now()}_${safeName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          url: downloadURL,
          path: snapshot.ref.fullPath,
          alt: file.name
        };
      });

      const newImages = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));
    } catch (err) {
      setError('Erreur lors du téléchargement des images');
      console.error(err);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = async (imageToRemove) => {
    try {
      if (imageToRemove.path) {
        const imageRef = ref(storage, imageToRemove.path);
        await deleteObject(imageRef);
      }
      
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter(img => img.url !== imageToRemove.url)
      }));
    } catch (err) {
      console.error('Erreur suppression image:', err);
      // Supprimer quand même de l'interface même si l'image storage échoue
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter(img => img.url !== imageToRemove.url)
      }));
    }
  };

  const generateSlug = (title) => {
    return title
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!formData.title_i18n.fr && !formData.title) {
        setError('Le titre français est requis');
        setIsLoading(false);
        return;
      }
      if (formData.price <= 0) {
        setError('Le prix doit être supérieur à 0');
        setIsLoading(false);
        return;
      }
      if (formData.images.length === 0) {
        setError('Au moins une image est requise');
        setIsLoading(false);
        return;
      }

      // S'assurer que les champs par défaut sont à jour
      const finalTitle = formData.title_i18n.fr || formData.title || '';
      const finalDescription = formData.description_i18n.fr || formData.description || '';

      // Préparer les données
      const productData = {
        title_i18n: formData.title_i18n,
        description_i18n: formData.description_i18n,
        title: finalTitle,
        description: finalDescription,
        price: formData.price,
        comparePrice: formData.comparePrice || 0,
        category: formData.category,
        sizes: formData.sizes,
        colors: formData.colors.filter(c => c.trim() !== ''),
        inventory: formData.inventory,
        featured: formData.featured,
        status,
        images: formData.images,
        slug: formData.slug || generateSlug(finalTitle),
        updatedAt: serverTimestamp()
      };

      // Ajouter createdAt si nouveau produit
      if (!id) {
        productData.createdAt = serverTimestamp();
      }

      // Sauvegarde
      if (id) {
        await setDoc(doc(db, 'products', id), productData, { merge: true });
        setSuccess(`Produit ${status === 'published' ? 'publié' : 'sauvegardé'} avec succès !`);
      } else {
        await addDoc(collection(db, 'products'), productData);
        setSuccess(`Produit créé et ${status === 'published' ? 'publié' : 'sauvegardé'} avec succès !`);
        setTimeout(() => navigate('/shop'), 2000);
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde : ' + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/shop')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            {id ? 'Modifier le produit' : 'Nouveau produit'}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={(e) => handleSubmit(e, 'draft')}
            disabled={loading}
            startIcon={<Save />}
          >
            Sauvegarder brouillon
          </Button>
          <Button
            variant="contained"
            onClick={(e) => handleSubmit(e, 'published')}
            disabled={loading}
            startIcon={<Save />}
          >
            Publier le produit
          </Button>
        </Box>
      </Box>

      {/* Alertes */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box component="form" onSubmit={(e) => handleSubmit(e, 'draft')}>
        <Grid container spacing={3}>
          {/* Contenu principal */}
          <Grid item xs={12} md={8}>
            {/* Sélecteur de langue */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Language />
                <Typography variant="subtitle1">Traductions</Typography>
              </Box>
              <Tabs 
                value={activeLang} 
                onChange={(e, newValue) => setActiveLang(newValue)}
                sx={{ mb: 2 }}
              >
                {LANGUAGES.map((lang) => (
                  <Tab 
                    key={lang.code} 
                    label={lang.label} 
                    value={lang.code}
                    icon={lang.code === 'fr' ? '⭐' : ''}
                  />
                ))}
              </Tabs>

              {/* Titre et description selon la langue */}
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={`Titre (${LANGUAGES.find(l => l.code === activeLang)?.label})`}
                  value={formData.title_i18n[activeLang] || ''}
                  onChange={(e) => handleLangChange('title', e.target.value, activeLang)}
                  required={activeLang === 'fr'}
                  sx={{ mb: 2 }}
                  helperText={activeLang === 'fr' ? "Le titre français est obligatoire" : "Traduction optionnelle"}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={`Description (${LANGUAGES.find(l => l.code === activeLang)?.label})`}
                  value={formData.description_i18n[activeLang] || ''}
                  onChange={(e) => handleLangChange('description', e.target.value, activeLang)}
                  helperText={activeLang === 'fr' ? "Description principale du produit" : "Traduction de la description"}
                />
              </Box>
            </Paper>

            {/* Informations de base */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Informations du produit</Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Prix (€) *"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Prix comparé (€)"
                    name="comparePrice"
                    value={formData.comparePrice}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Laissez 0 pour ne pas afficher de réduction"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Stock *"
                    name="inventory"
                    value={formData.inventory}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Catégorie *</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label="Catégorie *"
                      required
                    >
                      <MenuItem value=""><em>Sélectionner une catégorie</em></MenuItem>
                      {CATEGORY_OPTIONS.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.label.fr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Slug (URL)"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    helperText={`URL: /shop/${formData.slug || generateSlug(formData.title_i18n.fr || formData.title || '')}`}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Images */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Images du produit</Typography>
              
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="product-images"
                  multiple
                  type="file"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />
                <label htmlFor="product-images">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddPhotoAlternate />}
                    disabled={uploadingImages}
                  >
                    Ajouter des images
                  </Button>
                </label>
                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                  La première image sera l'image principale
                </Typography>
              </Box>

              {uploadingImages && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Téléchargement en cours...</Typography>
                </Box>
              )}

              {formData.images.length === 0 ? (
                <Box sx={{ 
                  border: '2px dashed #ddd', 
                  borderRadius: 2, 
                  p: 4, 
                  textAlign: 'center',
                  bgcolor: '#fafafa'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune image téléchargée. Ajoutez au moins une image pour votre produit.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {formData.images.map((image, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="140"
                          image={image.url}
                          alt={image.alt}
                          sx={{ objectFit: 'cover' }}
                        />
                        <Box sx={{ 
                          p: 1, 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            {index === 0 && "Principale"}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeImage(image)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* Métadonnées */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Options</Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                  />
                }
                label="Produit en vedette"
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tailles disponibles</InputLabel>
                <Select
                  multiple
                  value={formData.sizes}
                  onChange={handleSizeChange}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Couleurs</Typography>
                <Box display="flex" gap={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    placeholder="Ex: Rouge, Bleu, Noir..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" onClick={addColor}>
                    Ajouter
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {formData.colors.filter(c => c.trim() !== '').map((color, index) => (
                    <Chip
                      key={index}
                      label={color}
                      onDelete={() => removeColor(color)}
                      size="small"
                      sx={{ 
                        bgcolor: color.toLowerCase(), 
                        color: '#fff',
                        '& .MuiChip-deleteIcon': {
                          color: '#fff'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Statut</Typography>
                <FormControl fullWidth>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <MenuItem value="draft">Brouillon</MenuItem>
                    <MenuItem value="published">Publié</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="outlined"
                  onClick={(e) => handleSubmit(e, 'draft')}
                  disabled={loading}
                  startIcon={<Save />}
                  fullWidth
                >
                  Sauvegarder brouillon
                </Button>
                <Button
                  variant="contained"
                  onClick={(e) => handleSubmit(e, 'published')}
                  disabled={loading}
                  startIcon={<Save />}
                  fullWidth
                >
                  Publier le produit
                </Button>
              </Box>
            </Paper>

            {/* Aperçu */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Aperçu</Typography>
              <Box sx={{ 
                border: '1px solid #eee', 
                borderRadius: 1, 
                p: 2,
                bgcolor: '#fafafa'
              }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Titre: {formData.title_i18n.fr || formData.title || '(Non défini)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Prix: {formData.price}€
                  {formData.comparePrice > 0 && ` (au lieu de ${formData.comparePrice}€)`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stock: {formData.inventory} unités
                </Typography>
                {formData.category && (
                  <Typography variant="body2" color="text.secondary">
                    Catégorie: {formData.category}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default ProductForm;